import { ArrowLeft, Check, Download, ExternalLink, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import LeaveDocumentAnalysisSummary from "../components/LeaveDocumentAnalysisSummary";
import PageHeader from "../components/PageHeader";
import type { LeaveType, Paginated } from "../types/api";
import { unwrapResults } from "../types/api";
import type { LeaveDocument } from "../types/leaveDocument";
import {
  DECISION_OPTIONS,
  hasNothingToCompare,
  periodLabel,
} from "../types/leaveDocument";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate, formatMinutes } from "../utils/format";

const IGNORE_OPTION = "ignore";

export default function LeaveDocumentDetailPage() {
  const { documentId } = useParams();
  const [document, setDocument] = useState<LeaveDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [mappingSelections, setMappingSelections] = useState<Record<number, string>>(
    {},
  );
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<LeaveDocument>(`/leave/documents/${documentId}/`);
      setDocument(response.data);
      setError("");
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Deze weekstaat kon niet worden geladen."),
      );
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (document?.status !== "needs_input") {
      setLeaveTypes([]);
      return;
    }

    void api
      .get<Paginated<LeaveType> | LeaveType[]>("/leave/types/?scope=all")
      .then((response) => setLeaveTypes(unwrapResults(response.data)))
      .catch(() => setLeaveTypes([]));
  }, [document?.status]);

  const analysis = useMemo(() => document?.analyses?.[0] ?? null, [document]);
  const differences = analysis?.differences ?? [];

  useEffect(() => {
    setMappingSelections({});
  }, [analysis?.id]);

  const confirmMapping = async (index: number, selection: string) => {
    if (!document || !selection) return;
    const ignore = selection === IGNORE_OPTION;
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      await api.post(`/leave/documents/${document.id}/confirm/`, {
        kind: "unknown_term",
        index,
        action: ignore ? "ignore" : "",
        leave_type_id: ignore ? null : Number(selection),
      });
      await load();
      setSuccess(
        ignore
          ? "Deze regel wordt voortaan genegeerd."
          : "Het verloftype is opgeslagen en opnieuw verwerkt.",
      );
    } catch (requestError: unknown) {
      setError(apiErrorMessage(requestError, "De mapping kon niet worden opgeslagen."));
    } finally {
      setWorking(false);
    }
  };

  const confirmValue = async (index: number, value: string) => {
    if (!document || value === "") return;
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      await api.post(`/leave/documents/${document.id}/confirm/`, {
        kind: "leave_entry",
        index,
        value: Number(value),
      });
      await load();
      setSuccess("De waarde is opgeslagen en opnieuw verwerkt.");
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De onzekere waarde kon niet worden bevestigd."),
      );
    } finally {
      setWorking(false);
    }
  };

  const decide = async (differenceId: number, decision: string) => {
    if (!document || !decision) return;
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      await api.post(
        `/leave/documents/${document.id}/differences/${differenceId}/decision/`,
        { decision },
      );
      await load();
      setSuccess("De genomen actie is bijgewerkt.");
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De genomen actie kon niet worden opgeslagen."),
      );
    } finally {
      setWorking(false);
    }
  };

  const reanalyze = async () => {
    if (!document) return;
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      await api.post(`/leave/documents/${document.id}/analyze/`);
      await load();
      setSuccess("De weekstaat is opnieuw geanalyseerd.");
    } catch (requestError: unknown) {
      await load().catch(() => undefined);
      setError(
        apiErrorMessage(
          requestError,
          "De weekstaat kon niet opnieuw worden geanalyseerd.",
        ),
      );
    } finally {
      setWorking(false);
    }
  };

  const openOriginal = async (download: boolean) => {
    if (!document) return;
    setError("");
    try {
      const response = await api.get(
        `/leave/documents/${document.id}/download/${
          download ? "" : "?disposition=inline"
        }`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data as Blob);
      if (download) {
        const anchor = window.document.createElement("a");
        anchor.href = url;
        anchor.download = document.original_filename;
        anchor.click();
      } else {
        window.open(url, "_blank", "noopener");
      }
      // De blob blijft even geldig zodat een nieuw tabblad hem kan tonen.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het originele bestand kon niet worden geopend."),
      );
    }
  };

  if (loading) return <LoadingState label="Weekstaat laden..." />;
  if (!document) {
    return (
      <div>
        <PageHeader title="Weekstaat" description="Deze weekstaat bestaat niet." />
        {error && <ErrorNotice>{error}</ErrorNotice>}
        <Link className="button subtle" to="/app/documents">
          <ArrowLeft size={17} /> Terug naar weekstaat controleren
        </Link>
      </div>
    );
  }

  return (
    <div className="document-detail-page">
      <PageHeader
        eyebrow="Verlofcontrole"
        title={document.original_filename}
        description={`${periodLabel(document)}${
          document.employer_name ? ` · ${document.employer_name}` : ""
        } · geüpload op ${formatDate(document.uploaded_at.slice(0, 10))}`}
        actions={
          <div className="page-header-actions">
            <Link className="button subtle" to="/app/documents">
              <ArrowLeft size={17} /> Terug
            </Link>
            {document.status !== "processing" && (
              <button
                className="button subtle"
                type="button"
                disabled={working}
                onClick={() => void reanalyze()}
              >
                <RefreshCw size={17} /> Opnieuw analyseren
              </button>
            )}
            <button
              className="button subtle"
              type="button"
              onClick={() => void openOriginal(false)}
            >
              <ExternalLink size={17} /> Origineel openen
            </button>
            <button
              className="button primary"
              type="button"
              onClick={() => void openOriginal(true)}
            >
              <Download size={17} /> Downloaden
            </button>
          </div>
        }
      />

      {error && <ErrorNotice>{error}</ErrorNotice>}
      {document.error_message && <ErrorNotice>{document.error_message}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}

      {analysis &&
        (analysis.normalized_data.unknown_terms?.length ||
          analysis.normalized_data.uncertain_values?.length ||
          analysis.warnings.length > 0) && (
          <section className="surface document-feedback">
            <div className="balance-section-heading">
              <div>
                <h2>AI-bevindingen bevestigen</h2>
                <p>
                  Controleer deze herkende regels. Na bevestiging wordt de vergelijking
                  direct opnieuw uitgevoerd.
                </p>
              </div>
            </div>
            {analysis.warnings.map((warning, index) => (
              <p className="info-banner" key={`${warning}-${index}`}>
                {warning}
              </p>
            ))}
            {analysis.normalized_data.unknown_terms?.map((term, index) => (
              <label
                className="document-feedback-row"
                key={`${term.raw_term}-${index}`}
              >
                <span>Onbekend verloftype: {term.raw_term ?? term.value}</span>
                <div className="inline-form">
                  <select
                    value={mappingSelections[index] ?? ""}
                    disabled={working}
                    onChange={(event) =>
                      setMappingSelections((current) => ({
                        ...current,
                        [index]: event.target.value,
                      }))
                    }
                  >
                    <option value="">Kies bestaand verloftype</option>
                    {leaveTypes.map((leaveType) => (
                      <option key={leaveType.id} value={leaveType.id}>
                        {leaveType.name}
                      </option>
                    ))}
                    <option value={IGNORE_OPTION}>Negeren (geen actie)</option>
                  </select>
                  <button
                    className="button subtle"
                    type="button"
                    disabled={working || !mappingSelections[index]}
                    onClick={() =>
                      void confirmMapping(index, mappingSelections[index] ?? "")
                    }
                  >
                    Opslaan
                  </button>
                </div>
              </label>
            ))}
            {analysis.normalized_data.uncertain_values?.map((value, itemIndex) =>
              typeof value.index === "number" ? (
                <label
                  className="document-feedback-row"
                  key={`uncertain-${value.index}-${itemIndex}`}
                >
                  <span>
                    Onzekere waarde ({Math.round((value.confidence ?? 0) * 100)}%):{" "}
                    {value.source?.raw_text ?? "controleer de herkende verlofregel"}
                  </span>
                  <div className="inline-form">
                    <input
                      type="number"
                      step="0.5"
                      defaultValue={value.extracted_value ?? ""}
                      disabled={working}
                    />
                    <button
                      className="button subtle"
                      type="button"
                      disabled={working}
                      onClick={(event) => {
                        const input = event.currentTarget
                          .previousElementSibling as HTMLInputElement | null;
                        void confirmValue(value.index!, input?.value ?? "");
                      }}
                    >
                      Bevestigen
                    </button>
                  </div>
                </label>
              ) : null,
            )}
          </section>
        )}

      <section className="surface document-detail-summary">
        <div className="balance-section-heading">
          <div>
            <h2>Gegevensmatch</h2>
            <p>
              {document.difference_counts.total} regels vergeleken ·{" "}
              {document.difference_counts.mismatched} met verschil ·{" "}
              {document.difference_counts.open} nog te beoordelen.
            </p>
          </div>
          <span className={`member-status is-labelled status-${document.status}`}>
            {document.status_display || document.status}
          </span>
        </div>
        <LeaveDocumentAnalysisSummary
          document={document}
          analysis={analysis}
          showWarnings={false}
        />
        {differences.length === 0 ? (
          !(analysis && hasNothingToCompare(analysis)) && (
            <div className="empty-state compact">
              <Check size={25} />
              <h2>Nog geen vergelijking</h2>
              <p>
                {document.status === "needs_input"
                  ? "Bevestig eerst de AI-bevindingen hierboven."
                  : "Deze weekstaat is nog niet volledig geanalyseerd."}
              </p>
            </div>
          )
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Transactiedatum</th>
                  <th>Type mutatie</th>
                  <th>Verloftype</th>
                  <th>In de verlofkaart</th>
                  <th>In de week-/maandstaat</th>
                  <th>Verschil</th>
                  <th>Match</th>
                  <th>Genomen actie</th>
                </tr>
              </thead>
              <tbody>
                {differences.map((row) => (
                  <tr key={row.id} className={row.matched ? "" : "is-difference"}>
                    <td>
                      {row.transaction_date
                        ? formatDate(row.transaction_date)
                        : "Onbekend"}
                    </td>
                    <td>{row.mutation_label}</td>
                    <td>{row.leave_type_name || "-"}</td>
                    <td>{formatMinutes(row.app_minutes)}</td>
                    <td>{formatMinutes(row.document_minutes)}</td>
                    <td>{formatMinutes(row.difference_minutes)}</td>
                    <td>
                      {row.matched ? (
                        <span className="match-yes">
                          <Check size={16} /> Gelijk
                        </span>
                      ) : (
                        <span className="match-no">
                          <X size={16} /> Verschil
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        value={
                          DECISION_OPTIONS.some((option) => option.value === row.status)
                            ? row.status
                            : ""
                        }
                        disabled={working}
                        onChange={(event) => void decide(row.id, event.target.value)}
                      >
                        <option value="">
                          {row.matched ? "Geen actie nodig" : "Nog te beoordelen"}
                        </option>
                        {DECISION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="form-footnote">
          Een eerder overgenomen saldocorrectie wordt automatisch teruggeboekt zodra je
          hier een andere actie kiest. Een overname die een bestaande verlofregel heeft
          vervangen of geannuleerd kan alleen handmatig worden hersteld.
        </p>
      </section>
    </div>
  );
}
